import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { TOUR_STEPS as ALL_STEPS } from "../lib/tourSteps";
import { pathAllowedForUser } from "../pages/DashboardLayout";

const DONE_KEY = "dojova_tour_done";
const ACTIVE_KEY = "dojova_tour_step";
const PAD = 8;

function waitFor(selector, timeout = 3000) {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const tick = () => {
      const el = selector ? document.querySelector(selector) : null;
      if (el || !selector || Date.now() - t0 > timeout) return resolve(el);
      requestAnimationFrame(tick);
    };
    tick();
  });
}

// Visite guidée in-app : projecteur sur l'élément ciblé + carte explicative. Démarre via ?tour=1 ou le bouton « Visite guidée ».
export default function GuidedTour({ user }) {
  // Accueil selon les droits (admin restreint / démo → espace-admin) ; étapes limitées aux routes permises.
  const home = !user || pathAllowedForUser("/app", user) ? "/app" : "/app/espace-admin";
  const TOUR_STEPS = ALL_STEPS.filter((st) => (st.route === "/app" ? home === "/app" || st.key !== "matin" : !user || pathAllowedForUser(st.route.split("?")[0], user)))
    .map((st) => (st.route === "/app" ? { ...st, route: home } : st));
  const navigate = useNavigate();
  const navigatedFor = useRef(-1);
  const location = useLocation();
  const [step, setStep] = useState(() => { const s = sessionStorage.getItem(ACTIVE_KEY); return s === null ? -1 : Number(s); });
  const [rect, setRect] = useState(null);

  // Déclencheurs : ?tour=1, ou compte démo jamais visité.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("tour") === "1") { sessionStorage.setItem(ACTIVE_KEY, "0"); setStep(0); p.delete("tour"); navigate({ pathname: location.pathname, search: p.toString() }, { replace: true }); return; }
    if (step === -1 && user?.email?.includes("demo") && !localStorage.getItem(DONE_KEY)) { sessionStorage.setItem(ACTIVE_KEY, "0"); setStep(0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user?.email]);

  useEffect(() => {
    const onStart = () => { sessionStorage.setItem(ACTIVE_KEY, "0"); setStep(0); };
    window.addEventListener("dojova:tour", onStart);
    return () => window.removeEventListener("dojova:tour", onStart);
  }, []);

  const current = step >= 0 ? TOUR_STEPS[step] : null;

  // Positionnement : navigue vers la route de l'étape puis mesure la cible.
  useEffect(() => {
    if (!current) return;
    let alive = true;
    const [path, search] = current.route.split("?");
    const needsNav = location.pathname !== path || (search && !location.search.includes(search));
    if (needsNav && navigatedFor.current !== step) { navigatedFor.current = step; navigate(current.route); return; }
    (async () => {
      const el = await waitFor(current.target);
      if (!alive) return;
      if (el) { el.scrollIntoView({ block: "center", behavior: "smooth" }); await new Promise((r) => setTimeout(r, 350)); }
      if (!alive) return;
      setRect(el ? el.getBoundingClientRect() : null);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, location.pathname]);

  const finish = useCallback(() => { localStorage.setItem(DONE_KEY, "1"); sessionStorage.removeItem(ACTIVE_KEY); setStep(-1); setRect(null); }, []);
  const go = (n) => { if (n >= TOUR_STEPS.length) return finish(); navigatedFor.current = -1; sessionStorage.setItem(ACTIVE_KEY, String(n)); setRect(null); setStep(n); };

  useEffect(() => {
    if (!current) return;
    const onKey = (e) => { if (e.key === "Escape") finish(); if (e.key === "ArrowRight") go(step + 1); if (e.key === "ArrowLeft" && step > 0) go(step - 1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  if (!current) return null;
  const spot = rect && rect.width > 0 ? { left: rect.left - PAD, top: rect.top - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 } : null;
  const card = (() => {
    const W = 380;
    if (!spot) return { left: "50%", top: "50%", transform: "translate(-50%,-50%)" };
    const side = current.side || "bottom";
    const vw = window.innerWidth, vh = window.innerHeight;
    if (side === "right" && spot.left + spot.width + W + 24 < vw) return { left: spot.left + spot.width + 16, top: Math.min(Math.max(spot.top, 16), vh - 280) };
    if (side === "top" && spot.top > 260) return { left: Math.min(Math.max(spot.left, 16), vw - W - 16), top: spot.top - 16, transform: "translateY(-100%)" };
    const top = spot.top + spot.height + 16;
    return { left: Math.min(Math.max(spot.left, 16), vw - W - 16), top: top + 260 > vh ? Math.max(vh - 280, 16) : top };
  })();

  return (
    <div className="fixed inset-0 z-[200]" data-testid="guided-tour" aria-live="polite">
      {spot
        ? <div className="absolute rounded-xl ring-2 ring-amber-400 transition-all duration-300 pointer-events-none" style={{ ...spot, boxShadow: "0 0 0 9999px rgba(0,0,0,0.72)" }} data-testid="tour-spotlight" />
        : <div className="absolute inset-0 bg-black/75" />}
      <div className="absolute w-[380px] max-w-[calc(100vw-32px)] rounded-2xl border border-amber-500/40 bg-zinc-950 shadow-2xl p-5 text-zinc-100 animate-in fade-in zoom-in-95 duration-200" style={card} data-testid="tour-card">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] uppercase tracking-[0.25em] text-amber-400 font-semibold" data-testid="tour-counter">Visite guidée · {step + 1}/{TOUR_STEPS.length}</span>
          <button onClick={finish} aria-label="Quitter la visite" data-testid="tour-close" className="text-zinc-500 hover:text-zinc-100 transition-colors"><X size={16} /></button>
        </div>
        <h3 className="text-lg font-bold leading-tight" data-testid="tour-title">{current.title}</h3>
        <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{current.body}</p>
        <div className="mt-4 h-1 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-amber-500 transition-all duration-300" style={{ width: `${((step + 1) / TOUR_STEPS.length) * 100}%` }} /></div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <button onClick={finish} className="text-xs text-zinc-500 hover:text-zinc-200 transition-colors" data-testid="tour-skip">Passer</button>
          <div className="flex gap-2">
            {step > 0 && <button onClick={() => go(step - 1)} data-testid="tour-prev" className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-3 py-1.5 text-sm hover:border-zinc-500 transition-colors"><ArrowLeft size={14} /> Précédent</button>}
            <button onClick={() => go(step + 1)} data-testid="tour-next" className="inline-flex items-center gap-1 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-4 py-1.5 text-sm transition-colors">
              {step + 1 === TOUR_STEPS.length ? "Terminer" : "Suivant"} <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
